/**
 * PATH: packages/ui/src/kanban/components/card-detail/descriptionActions.ts
 * FILE_ID: UI-KANBAN-CARDDETAIL-DESCACTIONS-001
 * PURPOSE: Pure detectors that turn what an operator is typing into a
 *          card's DESCRIPTION into actionable suggestions — so the
 *          drawer can offer a "tap to apply" actions bar instead of the
 *          operator hunting for the assignee / due-date / checklist
 *          controls separately.
 * WHY:     Operator (Bravada, 2026-06-17), typing "0% complete today ron
 *          needs to …": "see when I am typing it should auto fill or
 *          create an actions bar.. to help sequence things properly.."
 *          Auto-detect three intents inline:
 *            - @name            → assign that person
 *            - today/Fri/…      → set the action date (next work day)
 *            - "- step" / "[]"  → add an ordered checklist step
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────────┐   ┌─────────────────┐
 *   │ description  │ → │ detectDescriptionActions│ → │ DetectedAction[]│
 *   │ text + ctx   │   │ (assign / due / step)   │   │ (tap to apply)  │
 *   └──────────────┘   └────────────────────────┘   └─────────────────┘
 * DEPENDENCIES: none (pure — fully unit-tested in descriptionActions.test.ts)
 * RELATED:
 *   - card-detail/DescriptionActionsBar.tsx: renders the chips + applies
 *   - api.ts: attachAssignee / updateCard(due_date) / createChecklistItem
 */

/** A person the detector can resolve an `@token` to. Mirrors the
 *  shape of `KanbanMember` we actually need (id + name) without
 *  importing the heavier type. */
export interface DescriptionMember {
  id: number
  name: string
}

export interface DescriptionActionsContext {
  /** Org/board member directory used to resolve `@name` → user id.
   *  Omitted (or empty) → no assign suggestions surface. */
  members?: DescriptionMember[]
  /** Injectable "now" so the date detector is deterministic in tests.
   *  Defaults to `new Date()` at call time. */
  now?: Date
}

export interface DetectedAssignAction {
  kind: 'assign'
  /** Stable identity used to dedupe + track "already applied". */
  key: string
  /** The raw matched token, e.g. "@ron". */
  raw: string
  userId: number
  name: string
}

export interface DetectedDueAction {
  kind: 'due'
  key: string
  /** The raw matched phrase, e.g. "tomorrow" / "next fri". */
  raw: string
  /** ISO calendar date (YYYY-MM-DD, local). */
  iso: string
  /** Human label for the chip, e.g. "Today" / "Fri 19 Jun". */
  label: string
}

export interface DetectedChecklistAction {
  kind: 'checklist'
  key: string
  /** The full matched line. */
  raw: string
  /** The step text with the bullet prefix stripped. */
  title: string
}

export type DetectedAction =
  | DetectedAssignAction
  | DetectedDueAction
  | DetectedChecklistAction

// ── date helpers (pure) ──────────────────────────────────────────────

const WEEKDAYS: Record<string, number> = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3, weds: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
}
const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}

/** Local YYYY-MM-DD (NOT UTC — avoids the off-by-one that
 *  `toISOString()` introduces for users behind UTC). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function dueLabel(d: Date, today: Date): string {
  const days = Math.round((startOfDay(d).getTime() - startOfDay(today).getTime()) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  return `${WD_SHORT[d.getDay()]} ${d.getDate()} ${MON_SHORT[d.getMonth()]}`
}

/** Soonest date matching `target` weekday that is >= today (so a bare
 *  "Friday" on a Friday means today). `forceNextWeek` (the "next"
 *  prefix) pushes a same-week hit into the following week. */
function nextWeekday(today: Date, target: number, forceNextWeek: boolean): Date {
  const base = startOfDay(today)
  let diff = (target - base.getDay() + 7) % 7
  if (forceNextWeek && diff < 7) diff += 7
  return addDays(base, diff)
}

interface DuePhraseMatch {
  raw: string
  iso: string
  label: string
  index: number
}

/**
 * Scan free text for the FIRST recognised due-date phrase (a card has
 * one due date). Recognises: ISO dates, today/tonight, tomorrow,
 * "next week", "next <weekday>", and bare <weekday> (full + common
 * abbreviations). Returns null when nothing matches.
 */
export function findFirstDuePhrase(
  text: string,
  now: Date = new Date(),
): { raw: string; iso: string; label: string } | null {
  if (!text) return null
  const today = startOfDay(now)
  const candidates: DuePhraseMatch[] = []

  const push = (raw: string, date: Date, index: number) => {
    candidates.push({ raw, iso: toIsoDate(date), label: dueLabel(date, today), index })
  }

  // ISO date (e.g. 2026-06-19) — accept only sane calendar values.
  for (const m of text.matchAll(/\b(\d{4})-(\d{2})-(\d{2})\b/g)) {
    const [, y, mo, d] = m
    const month = Number(mo)
    const day = Number(d)
    if (month < 1 || month > 12 || day < 1 || day > 31) continue
    push(m[0], new Date(Number(y), month - 1, day), m.index ?? 0)
  }

  // today / tonight
  for (const m of text.matchAll(/\b(today|tonight)\b/gi)) push(m[0], today, m.index ?? 0)
  // tomorrow + common shorthands
  for (const m of text.matchAll(/\b(tomorrow|tmrw|tmr|tomoz)\b/gi)) push(m[0], addDays(today, 1), m.index ?? 0)
  // next week
  for (const m of text.matchAll(/\bnext\s+week\b/gi)) push(m[0], addDays(today, 7), m.index ?? 0)
  // next <weekday>
  for (const m of text.matchAll(/\bnext\s+([a-z]+)\b/gi)) {
    const wd = WEEKDAYS[m[1].toLowerCase()]
    if (wd === undefined) continue
    push(m[0], nextWeekday(today, wd, true), m.index ?? 0)
  }
  // bare <weekday>
  for (const m of text.matchAll(/\b([a-z]+)\b/gi)) {
    const wd = WEEKDAYS[m[1].toLowerCase()]
    if (wd === undefined) continue
    // Skip when this is the tail of a "next <weekday>" (already handled
    // with the correct following-week semantics).
    const before = text.slice(0, m.index ?? 0)
    if (/\bnext\s+$/i.test(before)) continue
    push(m[0], nextWeekday(today, wd, false), m.index ?? 0)
  }

  if (candidates.length === 0) return null
  // Earliest in the text wins; on a tie prefer the longer (more
  // specific) phrase so "next friday" beats "friday".
  candidates.sort((a, b) => a.index - b.index || b.raw.length - a.raw.length)
  const best = candidates[0]
  return { raw: best.raw, iso: best.iso, label: best.label }
}

// ── member resolution (pure) ─────────────────────────────────────────

/** Resolve a bare `@token` to a member. Priority: an exact name-part
 *  match, then a name-part that starts with the token, then a
 *  whole-name substring. Returns null when nothing matches. */
export function resolveMemberToken(
  token: string,
  members: DescriptionMember[],
): DescriptionMember | null {
  const t = token.toLowerCase()
  if (!t) return null
  let startsWith: DescriptionMember | null = null
  let includes: DescriptionMember | null = null
  for (const m of members) {
    const name = m.name?.toLowerCase() ?? ''
    if (!name) continue
    const parts = name.split(/\s+/)
    if (parts.some((p) => p === t)) return m
    if (!startsWith && parts.some((p) => p.startsWith(t))) startsWith = m
    if (!includes && name.includes(t)) includes = m
  }
  return startsWith ?? includes
}

// ── checklist line detection (pure) ──────────────────────────────────

// A line is a checklist step when it starts with a bullet/checkbox
// marker: "- ", "* ", "• ", "[] ", "[ ] ", "- [ ] " (and the checked
// "[x]" forms, which we skip — an already-done step isn't a new step).
// The checkbox form (optionally bullet-prefixed) is tried FIRST so a
// "- [ ] step" consumes the whole "- [ ] " marker rather than the bare
// "- " bullet branch eating just the dash.
const STEP_PREFIX = /^\s*(?:(?:[-*]\s+)?\[\s*\]\s*|[-*•]\s+)/
const CHECKED_PREFIX = /^\s*(?:[-*]\s+)?\[\s*[xX]\s*\]/

// Forwarded / pasted email bodies materialise their headers as bullet lines
// ("- From: …", "- To: …", "- Sent: …", "- Subject: …", "- Cc: …"). The bare
// bullet detector would otherwise surface every one as a bogus "step"
// (operator 2026-06-23, screenshot: the DETECTED bar was full of
// From/To/Sent/Subject/Cc chips parsed straight out of an email instead of
// real action items). Skip header-shaped lines so only genuine "- do this"
// steps become checklist suggestions.
const EMAIL_HEADER_RE =
  /^(?:from|to|cc|bcc|sent|date|subject|reply-to|importance|priority)\s*:/i

// A pasted email also bullet-ises the rest of its scaffolding — the quote
// attribution, the "view in <provider>" / link lines, the sender/recipient
// contact lines, and bare timestamps. None of these are real "- do this"
// steps either (operator 2026-06-23: "trim the DETECTED chips to only real
// action items"). Each pattern matches a WHOLE line that is PURELY the
// artifact — never prose that merely mentions an address or a date — so a
// genuine step like "email sarah@x.com the PO" or "review 01/06 report"
// still surfaces.

//   "On Mon, 1 Jun 2026 at 1:43 PM Mark Newman <…> wrote:" and the short
//   "… wrote:" sign-off Gmail / Outlook inject on a reply.
const QUOTE_ATTRIBUTION_RE = /\bwrote:\s*$/i
//   "View in Gmail", a bare URL, or an angle-bracketed link ("<https://…>").
const LINK_LINE_RE = /^(?:view in\b|<?https?:\/\/)/i
//   Contact-only: "Name <addr@host>" or a bare "addr@host", nothing else.
const CONTACT_ONLY_RE =
  /^(?:[^@<>]*<[^@\s<>]+@[^@\s<>]+>|[^\s@]+@[^\s@]+\.[^\s@]+)\s*$/
//   A line that is ONLY a date and/or time stamp ("01/06/2026 1:43 PM",
//   "23 Mar 2026", "1:43 PM"). findFirstDuePhrase still lifts a real deadline
//   out of prose; this only kills standalone timestamp lines.
const TIMESTAMP_ONLY_RE =
  /^(?:\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{1,2}\s+[a-z]{3,9}\.?\s+\d{2,4}|\d{1,2}:\d{2}(?:\s*[ap]m)?)(?:\s*(?:at|,)?\s*\d{1,2}:\d{2}(?:\s*[ap]m)?)?\s*$/i

/** True when a checklist-shaped line is actually email-paste noise — a header,
 *  a "… wrote:" attribution, a link, a bare contact, or a lone timestamp — and
 *  therefore must NOT surface as a DETECTED step. High-precision by design:
 *  every branch anchors to the whole line so real steps are never dropped. */
export function looksLikeEmailNoise(title: string): boolean {
  return (
    EMAIL_HEADER_RE.test(title) ||
    QUOTE_ATTRIBUTION_RE.test(title) ||
    LINK_LINE_RE.test(title) ||
    CONTACT_ONLY_RE.test(title) ||
    TIMESTAMP_ONLY_RE.test(title)
  )
}

function extractChecklistTitle(line: string): string | null {
  if (CHECKED_PREFIX.test(line)) return null
  const m = STEP_PREFIX.exec(line)
  if (!m) return null
  const title = line.slice(m[0].length).trim()
  if (title.length === 0) return null
  if (looksLikeEmailNoise(title)) return null
  return title
}

// ── action-ish gate (pure) ───────────────────────────────────────────

/** Free, instant pre-gate: is this text worth an on-demand AI parse?
 *  Used by AiActionsBar only to EMPHASISE the "Structure with AI" button
 *  (it stays clickable regardless). Action-ish = the heuristic detector
 *  already found something OR an action verb appears — the model is most
 *  useful exactly when the heuristic missed a follow-up that has no
 *  `@mention` / bullet (e.g. "I need a follow up with Tim on Thursday"). */
const ACTION_VERB_RE =
  /\b(follow(?:[\s-]?up)?|chase|call|email|remind|send|book|review|check\s+(?:in|back)|circle\s+back|ping|reach\s+out|schedule)\b/i

export function looksActionable(
  text: string,
  members?: DescriptionMember[],
): boolean {
  const t = (text ?? '').trim()
  if (t.length < 4) return false
  if (detectDescriptionActions(t, { members }).length > 0) return true
  return ACTION_VERB_RE.test(t)
}

// ── public entrypoint ────────────────────────────────────────────────

/**
 * Detect every actionable intent in the current description text.
 * Pure: same inputs → same outputs (inject `ctx.now` for determinism).
 * Order: checklist steps (in text order) first — they are the primary
 * "sequence things" intent — then the single due date, then assignees.
 */
export function detectDescriptionActions(
  text: string,
  ctx: DescriptionActionsContext = {},
): DetectedAction[] {
  const out: DetectedAction[] = []
  if (!text || !text.trim()) return out

  // 1) Checklist steps — one per qualifying line, deduped by title.
  const seenTitles = new Set<string>()
  for (const line of text.split('\n')) {
    const title = extractChecklistTitle(line)
    if (!title) continue
    const lower = title.toLowerCase()
    if (seenTitles.has(lower)) continue
    seenTitles.add(lower)
    out.push({ kind: 'checklist', key: `checklist:${lower}`, raw: line, title })
  }

  // 2) Due date — first recognised phrase only.
  const due = findFirstDuePhrase(text, ctx.now)
  if (due) {
    out.push({ kind: 'due', key: `due:${due.iso}`, raw: due.raw, iso: due.iso, label: due.label })
  }

  // 3) Assignees — every distinct @token that resolves to a member.
  const members = ctx.members ?? []
  if (members.length > 0) {
    const seenUsers = new Set<number>()
    for (const m of text.matchAll(/@([a-zA-Z][\w.'-]*)/g)) {
      const member = resolveMemberToken(m[1], members)
      if (!member || seenUsers.has(member.id)) continue
      seenUsers.add(member.id)
      out.push({
        kind: 'assign',
        key: `assign:${member.id}`,
        raw: m[0],
        userId: member.id,
        name: member.name,
      })
    }
  }

  return out
}
